import React, { useEffect, useState } from 'react';
import { Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ChartColumn, ListChecks, Pencil, PiggyBank, ShieldAlert, TrendingDown, TrendingUp, Wallet } from 'lucide-react';
import * as hr from '../../api';
import { useHR } from '../../HRContext';
import Modal from '../../components/Modal';
import { Chip, Kpi } from '../attendance/shared';
import { DetailModal, Header, naira } from './shared';
import Loader from '../../components/Loader';

// HR Expenses > Budget Tracker (Stephanet's hrexpenses/Budget.jsx): department budgets against approved spend. Budgets are set directly
// by holders of the Budget permission.
const SORTS = [['department', 'Department Name'], ['month_spend', 'Actual Spend'], ['remaining', 'Remaining Budget'], ['pct_month', '% Used']];
const HEALTH = { Healthy: 'green', Good: 'blue', Warning: 'amber', 'Over Budget': 'red', 'No Budget': 'gray' };
const pct = v => (v === null || v === undefined ? '—' : `${v.toFixed(1)}%`);

export default function HrExpenseBudget() {
  const { notify } = useHR();
  const [d, setD] = useState(null);
  const [sort, setSort] = useState('department');
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [trendsFor, setTrendsFor] = useState(null);
  const [error, setError] = useState('');
  const load = () => hr.hreBudgets().then(setD).catch(e => setError(e.message));
  useEffect(() => { load(); }, []);
  if (error) return <div className="error">{error}</div>;
  if (!d) return <Loader title="Loading budgets…" />;
  const k = d.kpis;
  const rows = [...d.rows].sort((a, b) => (sort === 'department' ? a.department.localeCompare(b.department)
    : sort === 'remaining' ? (a.remaining ?? -Infinity) - (b.remaining ?? -Infinity) : (b[sort] ?? -1) - (a[sort] ?? -1)));
  return (
    <div className="mdr mds ael hre">
      <Header title="Budget Tracker" sub={`HR expense budgets by department · ${d.month} · spend counts approved expenses`} />
      <div className="md-kpis mds-kpis hre-kpis">
        <Kpi label="Total Monthly Budget" value={naira(k.budget)} Ico={PiggyBank} tone="blue" note={`${k.departments} departments`} />
        <Kpi label="Total Actual Spend" value={naira(k.actual)} Ico={Wallet} tone="indigo" note={`${pct(k.pct_used)} of budget`} i={1} />
        <Kpi label="Remaining Budget" value={naira(k.remaining)} Ico={TrendingUp} tone="green" note={`${pct(k.budget ? 100 - k.pct_used : 0)} available`} i={2} />
        <Kpi label="Departments at Risk" value={k.at_risk} Ico={ShieldAlert} tone={k.at_risk ? 'rose' : 'teal'} note={k.at_risk ? '80% or more of budget used' : 'All departments healthy'} i={3} />
      </div>
      {k.unbudgeted_spend > 0 && <div className="hre-note bad">{naira(k.unbudgeted_spend)} was spent this month by departments without a budget.</div>}
      <div className="hre-bar no-print">
        <span className="muted">Sort by:</span>
        {SORTS.map(([key, label]) => <button key={key} className={`mdr-btn ${sort === key ? 'on' : ''}`} onClick={() => setSort(key)}>{label}</button>)}
      </div>
      <div className="hre-brows">
        {rows.map(r => (
          <div key={r.department_id} className={`hre-brow-card h-${HEALTH[r.health]} ${trendsFor === r.department_id ? 'trends' : ''}`}>
            <div className="hre-brc-head">
              <h3>{r.department}</h3><span className="hre-code">{r.code}</span><span className={`hre-health t-${HEALTH[r.health]}`}>{r.health}</span>
              <span className="spacer" />
              {d.can.budget && <button className="mds-view ael-edit" title="Set budget" onClick={() => setEditing(r)}><Pencil size={12} /></button>}
              <b className="hre-brc-pct">{r.has_budget ? pct(r.pct_month) : '—'}</b>
            </div>
            {r.has_budget ? <div className="hre-brc-bars">
              <div><div className="hre-brc-lbl"><span>This Month</span><b>{pct(r.pct_month)}</b></div><div className="hre-bbar"><i style={{ width: `${Math.min(100, r.pct_month || 0)}%` }} /></div></div>
              <div><div className="hre-brc-lbl"><span>Year-to-Date</span><b>{pct(r.pct_ytd)}</b></div><div className={`hre-bbar ${r.pct_ytd > 100 ? 'over' : ''}`}><i style={{ width: `${Math.min(100, r.pct_ytd || 0)}%` }} /></div></div>
            </div> : <div className="hre-nobudget"><p>No budget set for this department.</p>
              {d.can.budget && <button className="mdr-btn" onClick={() => setEditing(r)}>Set Budget</button>}</div>}
            <div className="hre-brc-figs">
              <div><small>Monthly Budget</small><b>{naira(r.monthly_budget)}</b></div>
              <div><small>This Month Spend</small><b className="c-primary">{naira(r.month_spend)}</b></div>
              <div><small>Annual Cap</small><b>{naira(r.annual_cap)}</b></div>
              <div><small>YTD Spend</small><b className="c-green">{naira(r.ytd_spend)}</b></div>
            </div>
            <div className="hre-brc-tabs">
              <button className={trendsFor !== r.department_id ? 'on' : ''} onClick={() => setTrendsFor(t => (t === r.department_id ? null : t))}><ListChecks size={13} /> Current Month</button>
              <button className={trendsFor === r.department_id ? 'on' : ''} onClick={() => setTrendsFor(r.department_id)}><ChartColumn size={13} /> 6-Month Trends</button>
            </div>
            {trendsFor === r.department_id ? <Trends r={r} /> : <div className="hre-brc-foot">
              <div className="hre-brc-list">
                <small>Recent Expenses This Month ({r.month_items.length})</small>
                {r.month_items.length ? r.month_items.slice(0, 2).map(x => (
                  <button key={x.id} className="hre-brc-item" onClick={() => setViewing(x.id)}><span>{x.type} <em>{x.reference} · {x.date}</em></span><b>{naira(x.amount)}</b></button>
                )) : <p className="muted small">No approved expenses this month.</p>}
                {r.month_items.length > 2 && <p className="muted small">+{r.month_items.length - 2} more this month</p>}
                {r.pending.count > 0 && <p className="muted small">{r.pending.count} in review · {naira(r.pending.amount)}{r.pending.pct_after !== null ? ` · ${pct(r.pending.pct_after)} if approved` : ''}</p>}
              </div>
            </div>}
          </div>
        ))}
      </div>
      {viewing && <DetailModal id={viewing} notify={notify} onClose={() => setViewing(null)} onChanged={load} />}
      {editing && <BudgetModal r={editing} notify={notify} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

// 6-Month Trends: spending against the monthly budget for the last six months
const USAGE_TONE = { 'On Track': 'green', Warning: 'yellow', 'Over Budget': 'red', 'No Budget': 'gray' };
function Trends({ r }) {
  const t = r.trend;
  const avg = t.reduce((a, x) => a + x.actual, 0) / t.length;
  const last = t[t.length - 1]; const prev = t[t.length - 2];
  const mom = last.actual - prev.actual;
  const usages = t.filter(x => x.usage != null).map(x => x.usage);
  const avgUsage = usages.length ? usages.reduce((a, x) => a + x, 0) / usages.length : null;
  const adherence = avgUsage == null ? null : Math.max(0, 100 - avgUsage);
  const Arrow = mom > 0 ? TrendingUp : TrendingDown;
  return (
    <div className="hre-trends">
      <div className="hre-tstats">
        <div><small>6-Month Average</small><b>{naira(avg)}</b><em>{r.monthly_budget ? `${Math.round(avg / r.monthly_budget * 100)}% of budget` : 'No budget set'}</em></div>
        <div className={mom > 0 ? 'up' : 'down'}><small>Month-on-Month</small><b><Arrow size={15} /> {mom > 0 ? '+' : ''}{naira(mom)}</b><em>{mom > 0 ? 'Increase' : mom < 0 ? 'Decrease' : 'No change'} vs {prev.label}</em></div>
        <div><small>Current Month Usage</small><b>{last.usage == null ? '—' : `${Math.round(last.usage)}%`}</b><em>Of budget limit</em></div>
        <div className={adherence != null && adherence < 20 ? 'up' : 'down'}><small>Avg Budget Adherence</small><b>{adherence == null ? '—' : `${Math.round(adherence)}%`}</b><em>Budget left on average · last 6 months</em></div>
      </div>
      <div className="hre-tcharts">
        <div className="hre-tchart"><h4>6-Month Spending vs Budget</h4>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={t} margin={{ left: 8, right: 8, top: 6 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#d5dae1" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 10 }} tickFormatter={v => `₦${Math.round(v / 1000)}k`} width={62} />
              <Tooltip formatter={(v, n) => [naira(v), n]} /><Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="budget" name="Monthly Budget" fill="#b6bfcc" radius={[4, 4, 0, 0]} />
              <Bar dataKey="actual" name="Actual Spend" fill="#2563eb" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="hre-tchart"><h4>Budget Usage % Trend</h4>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={t} margin={{ left: 0, right: 12, top: 6 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#d5dae1" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 10 }} unit="%" width={46} domain={[0, dataMax => Math.max(110, Math.ceil(dataMax / 10) * 10)]} />
              <Tooltip formatter={v => [v == null ? '—' : `${v}%`, 'Usage']} /><Legend wrapperStyle={{ fontSize: 12 }} />
              <ReferenceLine y={80} stroke="#f59e0b" strokeDasharray="5 4" label={{ value: 'Warning 80%', fontSize: 10, fill: '#b45309', position: 'insideTopLeft' }} />
              <ReferenceLine y={100} stroke="#dc2626" strokeDasharray="5 4" label={{ value: 'Limit 100%', fontSize: 10, fill: '#b91c1c', position: 'insideTopLeft' }} />
              <Line type="monotone" dataKey="usage" name="Usage %" stroke="#0b318b" strokeWidth={2.5} dot={{ r: 4 }} activeDot={{ r: 6 }} connectNulls />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      <table className="hre-ttable">
        <thead><tr><th>Month</th><th className="r">Budget</th><th className="r">Actual Spend</th><th className="r">Variance</th><th className="r">Usage %</th><th>Status</th></tr></thead>
        <tbody>{t.map(x => (
          <tr key={x.label}><td><b>{x.label}</b></td><td className="r">{x.budget ? naira(x.budget) : '—'}</td><td className="r">{naira(x.actual)}</td>
            <td className={`r ${x.variance > 0 ? 'neg' : 'pos'}`}>{x.variance == null ? '—' : `${x.variance > 0 ? '+' : ''}${naira(x.variance)}`}</td>
            <td className="r">{x.usage == null ? '—' : `${Math.round(x.usage)}%`}</td><td><Chip tone={USAGE_TONE[x.status]}>{x.status}</Chip></td></tr>
        ))}</tbody>
      </table>
    </div>
  );
}

function BudgetModal({ r, notify, onClose, onSaved }) {
  const [f, setF] = useState({ monthly_budget: r.monthly_budget || '', annual_cap: r.annual_cap || '' });
  const [err, setErr] = useState({});
  const [busy, setBusy] = useState(false);
  const save = async () => {
    const e = {};
    if (f.monthly_budget === '' || Number(f.monthly_budget) < 0) e.monthly_budget = 'Enter the monthly budget (0 or more)';
    if (f.annual_cap !== '' && Number(f.annual_cap) < Number(f.monthly_budget)) e.annual_cap = 'Annual cap cannot be less than the monthly budget';
    setErr(e); if (Object.keys(e).length) return;
    setBusy(true);
    try {
      const res = await hr.hreSetBudget(r.department_id, { monthly_budget: Number(f.monthly_budget), annual_cap: f.annual_cap === '' ? null : Number(f.annual_cap) });
      notify(res.message); onSaved();
    } catch (x) { notify(x.message, 'bad'); } finally { setBusy(false); }
  };
  return (
    <Modal title={`Budget — ${r.department}`} onClose={onClose}>
      <div className="mdr-form wh-form">
        <div className="lvm-grid two">
          <label><span>Monthly Budget (₦) <b className="wh-req">*</b></span><input type="number" min="0" value={f.monthly_budget} onChange={e => setF({ ...f, monthly_budget: e.target.value })} />
            {err.monthly_budget && <small className="ael-err">{err.monthly_budget}</small>}</label>
          <label><span>Annual Cap (₦)</span><input type="number" min="0" value={f.annual_cap} placeholder={f.monthly_budget ? `${Number(f.monthly_budget) * 12} (12 × monthly)` : ''} onChange={e => setF({ ...f, annual_cap: e.target.value })} />
            {err.annual_cap ? <small className="ael-err">{err.annual_cap}</small> : <small className="muted">Blank = 12 × monthly budget</small>}</label>
        </div>
        <p className="muted small">This month so far: {naira(r.month_spend)} · Year-to-date: {naira(r.ytd_spend)}</p>
        <div className="mdh-form-foot wh-form-foot"><button className="mdr-btn" onClick={onClose}>Cancel</button><button className="mds-run" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save Budget'}</button></div>
      </div>
    </Modal>
  );
}
